import React from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { AuthContextProvider } from './AuthContext';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/**
 * Fallback shown in dev when VITE_CLERK_PUBLISHABLE_KEY is missing.
 */
const AuthNotConfiguredFallback: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gray-50">
    {import.meta.env.DEV && (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 fixed top-0 left-0 right-0 z-50">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              <strong>Clerk not configured.</strong>{' '}
              Set <code>VITE_CLERK_PUBLISHABLE_KEY</code> in your <code>.env</code> file.
            </p>
          </div>
        </div>
      </div>
    )}
    {children}
  </div>
);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider
 *
 * Wraps the application with Clerk's ClerkProvider and the app-level AuthContextProvider.
 * If the publishable key is missing, renders a dev-mode fallback banner instead.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  if (!CLERK_PUBLISHABLE_KEY) {
    return <AuthNotConfiguredFallback>{children}</AuthNotConfiguredFallback>;
  }

  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <AuthContextProvider>{children}</AuthContextProvider>
    </ClerkProvider>
  );
};

export default AuthProvider;
